import React from 'react';
import Select, { selectClasses } from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { Typography } from '@mui/joy';

type JoySelectFieldProps = {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  dropdownMaxHeight?: number | string;
};

export const JoySelectField: React.FC<JoySelectFieldProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder,
  className,
  disabled,
  required,
  error,
  dropdownMaxHeight = 240,
}) => {
  const labelId = React.useId();
  const buttonId = React.useId();
  const errorId = React.useId();

  return (
    <div className={className}>
      <Typography
        component="label"
        id={labelId}
        level="body-sm"
        fontWeight={500}
        sx={{ mb: '3px', display: 'block' }}
      >
        {label}
        {required && <span className="text-red-500"> *</span>}
      </Typography>

      <Select<string>
        value={value}
        onChange={(_event, newValue) => onChange(newValue)}
        placeholder={placeholder}
        indicator={<KeyboardArrowDown />}
        disabled={disabled}
        slotProps={{
          button: {
            id: buttonId,
            'aria-labelledby': `${labelId} ${buttonId}`,
            'aria-required': required || undefined,
            'aria-invalid': (!!error) || undefined,
            'aria-errormessage': error ? errorId : undefined,
          },
          listbox: {
            sx: {
              maxHeight: dropdownMaxHeight,
              overflow: 'auto',
            },
          },
        }}
        sx={{
          width: '100%',
          borderRadius: '0.375rem',
          minHeight: '42px',
          backgroundColor: '#fff',
          '--joy-palette-neutral-outlinedBorder': error ? '#ef4444' : '#d1d5db',
          '&:hover': {
            backgroundColor: '#fff',
          },
          [`& .${selectClasses.indicator}`]: {
            transition: '0.2s',
            [`&.${selectClasses.expanded}`]: {
              transform: 'rotate(-180deg)',
            },
          },
        }}
      >
        {options.map((opt) => (
          <Option key={opt.value} value={opt.value}>
            {opt.label}
          </Option>
        ))}
      </Select>

      {error && (
        <div id={errorId} className="mt-1 text-xs text-red-600">
          {error}
        </div>
      )}
    </div>
  );
};